import React from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Grid,
  CircularProgress,
  Collapse,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@material-ui/core'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@material-ui/icons'

const coverageChipProps = (status) => {
  switch (status) {
    case 'covered':
      return { label: 'Covered', style: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'partial':
      return { label: 'Partial', style: { backgroundColor: '#ff9800', color: '#fff' } }
    case 'missing':
      return { label: 'Missing', style: { backgroundColor: '#f44336', color: '#fff' } }
    default:
      return { label: status || 'Unknown', color: 'default' }
  }
}

const MaterialCatalogTable = ({ url }) => {
  const [materials, setMaterials] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [categories, setCategories] = React.useState([])
  const [categoryFilter, setCategoryFilter] = React.useState('All')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [expandedId, setExpandedId] = React.useState(null)
  const [editing, setEditing] = React.useState(null) // { _id, displayName, displayCategory, unit, manualPrice }
  const [createModal, setCreateModal] = React.useState(false)
  const [createData, setCreateData] = React.useState({ displayName: '', displayCategory: '', unit: 'Each', manualPrice: '' })
  const [deleteTarget, setDeleteTarget] = React.useState(null)
  const [seeding, setSeeding] = React.useState(false)
  const [seedResult, setSeedResult] = React.useState(null)

  React.useEffect(() => {
    fetchCategories()
  }, [])

  React.useEffect(() => {
    fetchMaterials()
  }, [categoryFilter])

  const fetchCategories = () => {
    axios.get(`${url}/api/bid-estimation/catalog/extended/categories`).then(res => {
      setCategories(Array.isArray(res.data) ? res.data : [])
    }).catch(console.error)
  }

  const fetchMaterials = () => {
    setLoading(true)
    const params = {}
    if (categoryFilter !== 'All') params.category = categoryFilter
    axios.get(`${url}/api/bid-estimation/catalog/extended`, { params }).then(res => {
      setMaterials(res.data || [])
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const filteredMaterials = React.useMemo(() => {
    if (!searchTerm) return materials
    const term = searchTerm.toLowerCase()
    return materials.filter(m =>
      (m.displayName || '').toLowerCase().includes(term) ||
      (m.aspireItemName || '').toLowerCase().includes(term) ||
      (m.aspireItemCode || '').toLowerCase().includes(term)
    )
  }, [materials, searchTerm])

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const handleEditStart = (item) => {
    setEditing({
      _id: item._id,
      displayName: item.displayName || '',
      displayCategory: item.displayCategory || '',
      unit: item.unit || '',
      manualPrice: item.manualPrice ?? ''
    })
  }

  const handleEditSave = () => {
    if (!editing) return
    const payload = {
      displayName: editing.displayName,
      displayCategory: editing.displayCategory,
      unit: editing.unit,
      manualPrice: editing.manualPrice === '' ? null : Number(editing.manualPrice)
    }
    axios.put(`${url}/api/bid-estimation/catalog/extended/${editing._id}`, payload).then(() => {
      setEditing(null)
      fetchMaterials()
      fetchCategories()
    }).catch(console.error)
  }

  const handleToggleHidden = (item) => {
    axios.put(`${url}/api/bid-estimation/catalog/extended/${item._id}`, {
      hidden: !item.hidden
    }).then(() => fetchMaterials()).catch(console.error)
  }

  const handleCreate = () => {
    const payload = {
      displayName: createData.displayName,
      displayCategory: createData.displayCategory || 'General',
      unit: createData.unit || 'Each',
      manualPrice: createData.manualPrice ? Number(createData.manualPrice) : null
    }
    axios.post(`${url}/api/bid-estimation/catalog/extended`, payload).then(() => {
      setCreateModal(false)
      setCreateData({ displayName: '', displayCategory: '', unit: 'Each', manualPrice: '' })
      fetchMaterials()
      fetchCategories()
    }).catch(console.error)
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/catalog/extended/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      fetchMaterials()
    }).catch(console.error)
  }

  const handleSeedFromAspire = () => {
    setSeeding(true)
    setSeedResult(null)
    axios.post(`${url}/api/bid-estimation/catalog/seed-from-aspire?availableToBid=true`).then(res => {
      setSeedResult(res.data)
      setSeeding(false)
      fetchMaterials()
      fetchCategories()
    }).catch(err => {
      setSeedResult({ error: err.response?.data?.error || err.message })
      setSeeding(false)
    })
  }

  if (loading && materials.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="All">All ({materials.length})</MenuItem>
              {categories.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon style={{ marginRight: 8, color: '#999' }} fontSize="small" />
            }}
          />
        </Grid>
        <Grid item style={{ flex: 1 }} />
        <Grid item>
          <Button
            variant="outlined"
            size="small"
            startIcon={seeding ? <CircularProgress size={14} /> : <AddIcon />}
            onClick={handleSeedFromAspire}
            disabled={seeding}
            style={{ marginRight: 8 }}
          >
            Seed from Aspire
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateModal(true)}
          >
            Add Item
          </Button>
        </Grid>
      </Grid>

      {seedResult && (
        <Box mb={2} p={1.5} style={{
          backgroundColor: seedResult.error ? '#ffebee' : '#e8f5e9',
          borderRadius: 4
        }}>
          <Typography variant="body2">
            {seedResult.error || seedResult.message}
          </Typography>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Category</TableCell>
            <TableCell align="right">Best Price ($)</TableCell>
            <TableCell align="right">Aspire Cost ($)</TableCell>
            <TableCell align="center">Coverage</TableCell>
            <TableCell align="center">Vendors</TableCell>
            <TableCell align="center">Aspire</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredMaterials.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="body2" style={{ padding: 20 }}>
                  No materials found. Use "Seed from Aspire" to populate from your Aspire catalog.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredMaterials.map(item => {
              const chipProps = coverageChipProps(item.coverageStatus)
              const isEditing = editing && editing._id === item._id
              const isExpanded = expandedId === item._id
              return (
                <React.Fragment key={item._id}>
                  <TableRow
                    hover
                    style={{
                      cursor: 'pointer',
                      opacity: item.hidden ? 0.5 : 1
                    }}
                    onClick={() => !isEditing && handleToggleExpand(item._id)}
                  >
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editing.displayName}
                          onChange={e => setEditing({ ...editing, displayName: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 250 }}
                        />
                      ) : (
                        <span>
                          {item.displayName}
                          {item.aspireItemName && item.displayName !== item.aspireItemName && (
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block', fontSize: 10 }}>
                              Aspire: {item.aspireItemName}
                            </Typography>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editing.unit}
                          onChange={e => setEditing({ ...editing, unit: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 80 }}
                        />
                      ) : (
                        item.unit || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editing.displayCategory}
                          onChange={e => setEditing({ ...editing, displayCategory: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 180 }}
                        />
                      ) : (
                        <Chip size="small" label={item.displayCategory || '—'} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editing.manualPrice}
                          onChange={e => setEditing({ ...editing, manualPrice: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 80 }}
                          placeholder="Override"
                        />
                      ) : (
                        item.bestPrice != null ? `$${Number(item.bestPrice).toFixed(2)}` : '—'
                      )}
                    </TableCell>
                    <TableCell align="right" style={{ color: '#999', fontSize: 12 }}>
                      {item.aspireCost != null ? `$${Number(item.aspireCost).toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={chipProps.label}
                        style={chipProps.style || {}}
                      />
                    </TableCell>
                    <TableCell align="center">{item.vendorCount || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={item.isLinkedToAspire ? `Aspire: ${item.aspireItemCode || item.aspireItemName}` : 'Not linked to Aspire'}>
                        {item.isLinkedToAspire
                          ? <LinkIcon fontSize="small" style={{ color: '#4caf50' }} />
                          : <LinkOffIcon fontSize="small" style={{ color: '#bdbdbd' }} />
                        }
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={handleEditSave}>
                            <SaveIcon fontSize="small" style={{ color: '#4caf50' }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditing(null)}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => handleEditStart(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleToggleHidden(item)}>
                            {item.hidden
                              ? <VisibilityIcon fontSize="small" style={{ color: '#999' }} />
                              : <VisibilityOffIcon fontSize="small" style={{ color: '#999' }} />
                            }
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteTarget(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <VendorPricesPanel item={item} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })
          )}
        </TableBody>
      </Table>

      {/* Create local item dialog */}
      <Dialog open={createModal} onClose={() => setCreateModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Catalog Item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Create a local item not yet in Aspire. You can link it to Aspire later.
          </Typography>
          <TextField
            label="Item Name"
            value={createData.displayName}
            onChange={e => setCreateData({ ...createData, displayName: e.target.value })}
            fullWidth margin="normal" variant="outlined" autoFocus
          />
          <TextField
            label="Category"
            value={createData.displayCategory}
            onChange={e => setCreateData({ ...createData, displayCategory: e.target.value })}
            fullWidth margin="normal" variant="outlined"
            placeholder="e.g., 360 - Irrigation Materials"
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                value={createData.unit}
                onChange={e => setCreateData({ ...createData, unit: e.target.value })}
                fullWidth margin="normal" variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Price ($)"
                type="number"
                value={createData.manualPrice}
                onChange={e => setCreateData({ ...createData, manualPrice: e.target.value })}
                fullWidth margin="normal" variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModal(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            color="primary"
            disabled={!createData.displayName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Catalog Item</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "{deleteTarget?.displayName}"? This removes it from your catalog but does not affect Aspire.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const VendorCatalogSources = ({ aspireItemId }) => {
  const [sources, setSources] = React.useState(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (!aspireItemId) { setLoading(false); return }
    setLoading(true)
    axios.get(`/api/vendor-catalogs/mappings/reverse/${aspireItemId}`)
      .then(res => { setSources(res.data || []); setLoading(false) })
      .catch(() => { setSources([]); setLoading(false) })
  }, [aspireItemId])

  if (loading) return <CircularProgress size={18} style={{ margin: 8 }} />
  if (!sources || sources.length === 0) {
    return <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>No vendor catalog sources linked</Typography>
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Product</TableCell>
          <TableCell>Vendor</TableCell>
          <TableCell>SKU</TableCell>
          <TableCell align="center">Page</TableCell>
          <TableCell>Catalog</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sources.map((s, i) => (
          <TableRow key={i}>
            <TableCell>{s.productName || '—'}</TableCell>
            <TableCell>{s.vendorName || '—'}</TableCell>
            <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.vendorSku || '—'}</TableCell>
            <TableCell align="center">{s.pageNumber ?? '—'}</TableCell>
            <TableCell>{s.catalogName ? `${s.catalogName} (${s.catalogVersion})` : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const VendorPricesPanel = ({ item }) => {
  const vendorPrices = item.vendorPrices || []

  return (
    <Box py={2} px={1}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Details</Typography>
          <Table size="small">
            <TableBody>
              {item.aspireItemName && (
                <TableRow>
                  <TableCell style={{ fontWeight: 600, width: 140 }}>Aspire Name</TableCell>
                  <TableCell>{item.aspireItemName}</TableCell>
                </TableRow>
              )}
              {item.aspireItemCode && (
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Aspire Code</TableCell>
                  <TableCell>{item.aspireItemCode}</TableCell>
                </TableRow>
              )}
              {item.aspireCost != null && (
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Aspire Cost</TableCell>
                  <TableCell>${Number(item.aspireCost).toFixed(2)}</TableCell>
                </TableRow>
              )}
              {item.manualPrice != null && (
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Manual Override</TableCell>
                  <TableCell>${Number(item.manualPrice).toFixed(2)}</TableCell>
                </TableRow>
              )}
              {item.notes && (
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Notes</TableCell>
                  <TableCell>{item.notes}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Vendor Prices ({vendorPrices.length})
          </Typography>
          {vendorPrices.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No vendor quotes yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Price ($)</TableCell>
                  <TableCell>Last Quote</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorPrices.map((vp, i) => (
                  <TableRow key={i}>
                    <TableCell>{vp.vendor || '—'}</TableCell>
                    <TableCell align="right">
                      {vp.latestPrice != null ? `$${Number(vp.latestPrice).toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell>
                      {vp.lastQuoteDate ? new Date(vp.lastQuoteDate).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Grid>
      </Grid>
      {item.aspireItemId && (
        <Box mt={2}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Vendor Catalog Sources
          </Typography>
          <VendorCatalogSources aspireItemId={item.aspireItemId} />
        </Box>
      )}
    </Box>
  )
}

export default MaterialCatalogTable
